import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormRecord,
  ValidatorFn,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, debounceTime, filter, takeUntil } from 'rxjs';
import { AxiosError } from 'axios';
import { format } from 'date-fns';
import {
  BatchStatus,
  BulkUpload,
  BulkUploadService,
  CreateBulkUploadParams,
} from 'src/app/services/bulk-upload/bulk-upload.service';
import {
  ClientsService,
  ListClientItem,
} from 'src/app/services/clients/clients.service';
import { EnumsService } from 'src/app/services/enums/enums.service';
import { HelpersService } from 'src/app/services/helpers/helpers.service';
import {
  ListPartnersResponse,
  PartnerService,
} from 'src/app/services/partner/partner.service';
import { ClaimType, LegalRelationship, PartyType } from 'src/types';
import { errorResponse } from 'src/types/error-response';
import * as xlsx from 'xlsx';
import {
  claimType,
  legalRelationship,
  partyType,
} from '../enums/enum-descriptors';
import {
  INTEREST_TYPE,
  InterestType,
} from 'src/app/services/cases/cases.service';
import { PayeeFormFieldComponent } from '../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeAutocompleteComponent } from '../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { PayeeIconButtonComponent } from '../../../shared/payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeIconComponent } from '../../../shared/payee-icon/payee-icon.component';
import { PayeeDropdownComponent } from '../../../shared/payee-form-fields/payee-dropdown/payee-dropdown.component';
import { PayeeDropzoneComponent } from '../../../shared/payee-dropzone/payee-dropzone.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';
import { NgTemplateOutlet, NgClass, KeyValuePipe } from '@angular/common';
import { CustomCheckboxComponent } from '../../custom-checkbox/custom-checkbox.component';

type ColumnOption = {
  columnId: string;
  label: string;
};

type CreationStrategyOption = {
  value: CreateBulkUploadParams['creation_strategy'];
  label: string;
};

type ProductTypeOption = {
  value: CreateBulkUploadParams['product_type'];
  label: string;
};

type CaseTypeOption = {
  value: 'invoice' | 'contract';
  label: string;
};

type BulkUploadControls = {
  client: FormControl<ListClientItem | null>;
  partner: FormControl<ListPartnersResponse['partners'][number] | null>;
  file: FormControl<File | null>;
  claim_file: FormControl<File | null>;
  creation_strategy: FormControl<CreationStrategyOption | null>;
  name: FormControl<string | null>;
  debtor_party_type: FormControl<PartyType | null>;
  claim_type: FormControl<ClaimType | null>;
  legal_relationship: FormControl<LegalRelationship | null>;
  product_type: FormControl<ProductTypeOption | null>;
  case_type: FormControl<CaseTypeOption | null>;
  interest_type: FormControl<InterestType | null>;
  column_associations: FormGroup<
    Record<string, FormControl<ColumnOption | null>>
  >;
  payee_case_references: FormGroup<Record<string, FormControl<boolean>>>;
  claim_references: FormGroup<Record<string, FormControl<boolean>>>;
};

@Component({
  selector: 'app-bulk-upload',
  templateUrl: './bulk-upload.component.html',
  styleUrls: ['./bulk-upload.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeAutocompleteComponent,
    PayeeIconButtonComponent,
    PayeeIconComponent,
    PayeeDropdownComponent,
    PayeeDropzoneComponent,
    MatProgressSpinner,
    PayeeButtonComponent,
    NgTemplateOutlet,
    NgClass,
    MatPaginator,
    CustomCheckboxComponent,
    KeyValuePipe,
  ],
})
export class BulkUploadComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly helpersService = inject(HelpersService);
  private readonly clientsService = inject(ClientsService);
  private readonly partnerService = inject(PartnerService);
  private readonly snackbar = inject(MatSnackBar);
  private readonly bulkUploadService = inject(BulkUploadService);
  private readonly enumsService = inject(EnumsService);

  private readonly destroy = new Subject<void>();
  private destroyed = false;

  readonly loading = signal(false);
  readonly collapsed = signal(false);
  bulkUpload?: BulkUpload;
  batchStatus?: BatchStatus;
  batchInterval?: ReturnType<typeof setInterval>;
  validatingData = false;

  readonly searchPartners = new Subject<string>();
  partnerOptions: ListPartnersResponse['partners'] = [];

  readonly searchClients = new Subject<string>();
  clientOptions: ListClientItem[] = [];

  readonly client = new FormControl<ListClientItem | null>(
    null,
    Validators.required,
  );
  readonly partner = new FormControl<
    ListPartnersResponse['partners'][number] | null
  >(null);
  readonly file = new FormControl<File | null>(null, Validators.required);
  readonly claim_file = new FormControl<File | null>(null, Validators.required);
  readonly creation_strategy = new FormControl<CreationStrategyOption | null>(
    null,
    Validators.required,
  );
  readonly name = new FormControl<string | null>(null, Validators.required);
  readonly debtor_party_type = new FormControl<PartyType | null>(null);
  readonly claim_type = new FormControl<ClaimType | null>(null);
  readonly legal_relationship = new FormControl<LegalRelationship | null>(null);
  readonly product_type = new FormControl<ProductTypeOption | null>(null);
  readonly case_type = new FormControl<CaseTypeOption | null>(null);
  readonly interest_type = new FormControl<InterestType | null>(null);
  readonly column_associations = new FormRecord<
    FormControl<ColumnOption | null>
  >({});
  readonly payee_case_references = new FormRecord<FormControl<boolean>>(
    {},
    this.requiredTrueInGroup,
  );
  readonly claim_references = new FormRecord<FormControl<boolean>>(
    {},
    this.requiredTrueInGroup,
  );

  columnOptions: ColumnOption[] = [];

  readonly interestTypeOptions = Object.values(INTEREST_TYPE);
  partyTypeOptions: PartyType[] = [];
  claimTypeOptions: ClaimType[] = [];
  legalRelationshipOptions: LegalRelationship[] = [];

  readonly productTypeOptions: ProductTypeOption[] = [
    { label: 'Fizetési felszólítás', value: 'notice' },
    { label: 'Ügyvédi felszólító levél', value: 'demand_letter' },
    { label: 'Fizetési meghagyás', value: 'hard_1' },
  ];

  readonly caseTypeOptions: CaseTypeOption[] = [
    { label: 'Számla alapú ügy', value: 'invoice' },
    { label: 'Szerződés alapú ügy', value: 'contract' },
  ];

  readonly creationStrategyOptions: CreationStrategyOption[] = [
    { value: 'pro_forma', label: 'Díjbekérő' },
    { value: 'start', label: 'Azonalli indítás' },
  ];

  requiredColumns: Record<string, string> = {};
  optionalColumns: Record<string, string> = {};
  errorMessages: string[] = [];
  paginatedErrorMessages: string[] = [];
  pageSize = 25;
  pageIndex = 0;
  columnExampleValues: Record<string, string> = {};

  readonly form = new FormGroup<BulkUploadControls>({
    client: this.client,
    partner: this.partner,
    file: this.file,
    claim_file: this.claim_file,
    creation_strategy: this.creation_strategy,
    name: this.name,
    debtor_party_type: this.debtor_party_type,
    claim_type: this.claim_type,
    legal_relationship: this.legal_relationship,
    product_type: this.product_type,
    case_type: this.case_type,
    interest_type: this.interest_type,
    column_associations: this.column_associations,
    payee_case_references: this.payee_case_references,
    claim_references: this.claim_references,
  });

  get filledRequiredAssociationsCount(): number {
    let count = 0;
    for (const columnName of Object.keys(this.requiredColumns)) {
      const control = this.column_associations.controls[columnName];
      if (control && control.value) {
        count++;
      }
    }
    return count;
  }

  get filledOptionalAssociationsCount(): number {
    let count = 0;
    for (const columnName of Object.keys(this.optionalColumns)) {
      const control = this.column_associations.controls[columnName];
      if (control && control.value) {
        count++;
      }
    }
    return count;
  }

  async ngOnInit(): Promise<void> {
    this.searchPartners
      .pipe(
        takeUntil(this.destroy),
        filter((v) => v.length >= 3),
        debounceTime(1000),
      )
      .subscribe(async (name) => await this.setPartnerOptions(name));

    this.searchClients
      .pipe(
        takeUntil(this.destroy),
        filter((v) => v.length >= 3),
        debounceTime(1000),
      )
      .subscribe(async (name) => await this.setClientOptions(name));

    const bulkUploadId = this.route.snapshot.queryParams.bulk_upload_id;
    if (bulkUploadId) {
      // Create a minimal bulk upload object for polling
      this.bulkUpload = {
        id: parseInt(bulkUploadId),
        last_start_batch_id: null,
        last_validation_batch_id: null,
        state: 'validating',
        name: '',
      };
      this.pollStatus();
    }

    await Promise.allSettled([
      this.setColumns(),
      this.setPartyTypeOptions(),
      this.setClaimTypeOptions(),
      this.setLegalRelationshipOptions(),
      this.setResult(),
    ]);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy.next();
    this.destroy.complete();
    this.stopPolling();
  }

  toggleCollapse(): void {
    this.collapsed.update((value) => !value);
  }

  expandForm(): void {
    this.collapsed.set(false);
  }

  clearErrorMessages(): void {
    this.errorMessages = [];
  }

  onPageChange(event: PageEvent): void {
    this.paginatedErrorMessages = this.errorMessages.slice(
      event.pageSize * event.pageIndex,
      event.pageSize * (event.pageIndex + 1),
    );
  }

  fileDropped(files: FileList): void {
    const file = files.item(0);
    this.file.markAsDirty();
    this.file.setValue(file);

    if (file) {
      this.extractHeadersFromFile(file);
    }
  }

  deleteFile(): void {
    this.file.markAsDirty();
    this.file.setValue(null);

    this.columnOptions = [];
    this.columnExampleValues = {};
    for (const control of Object.values(this.column_associations.controls)) {
      control.setValue(null);
    }
  }

  claimFileDropped(files: FileList): void {
    const file = files.item(0);
    this.claim_file.markAsDirty();
    this.claim_file.setValue(file);
  }

  deleteClaimFile(): void {
    this.claim_file.markAsDirty();
    this.claim_file.setValue(null);
  }

  partnerDisplayWith(
    partner?: ListPartnersResponse['partners'][number],
  ): string {
    return partner?.name ?? '';
  }

  clientDisplayWith(client?: ListClientItem): string {
    return client?.name ?? '';
  }

  columnDisplayWith(column?: ColumnOption): string {
    if (!column) {
      return '';
    }
    return `${column.columnId} - ${column.label}`;
  }

  partyTypeDisplayWith(partyType?: PartyType): string {
    return partyType?.label ?? '';
  }

  claimTypeDisplayWith(claimType?: ClaimType): string {
    return claimType?.label ?? '';
  }

  legalRelationshipDisplayWith(legalRelationship?: LegalRelationship): string {
    return legalRelationship?.label ?? '';
  }

  creationStrategyDisplayWith(
    creationStrategy?: CreationStrategyOption,
  ): string {
    return creationStrategy?.label ?? '';
  }

  productTypeDisplayWith(productType?: ProductTypeOption): string {
    return productType?.label ?? '';
  }

  caseTypeDisplayWith(caseType?: CaseTypeOption): string {
    return caseType?.label ?? '';
  }

  interestTypeDisplayWith(interestType?: InterestType): string {
    switch (interestType) {
      case 'contractual':
        return 'Szerződéses';
      case 'statutory':
        return 'Törvényes';
      default:
        return '';
    }
  }

  getExampleValue(columnOption?: ColumnOption): string {
    if (!columnOption) {
      return '';
    }
    return this.columnExampleValues[columnOption.columnId] || '';
  }

  async submit(): Promise<void> {
    if (this.loading()) {
      return;
    }

    this.clearErrorMessages();

    if (!this.form.valid) {
      this.helpersService.markAllChildrenAsDirty(this.form);
      this.expandForm();
      return;
    }

    try {
      this.loading.set(true);
      this.collapsed.set(true);
      this.validatingData = true;

      const column_associations: Record<string, string> = {};
      for (const [column, association] of Object.entries(
        this.column_associations.value,
      )) {
        if (association) {
          column_associations[column] = association.label;
        }
      }

      const debtorPartyType: PartyType | null = this.debtor_party_type.value;
      const claimType: ClaimType | null = this.claim_type.value;
      const legalRelationship: LegalRelationship | null =
        this.legal_relationship.value;
      const productType: ProductTypeOption | null = this.product_type.value;
      const caseType: CaseTypeOption | null = this.case_type.value;
      const payee_case_references: string[] = [];
      for (const [key, checked] of Object.entries(
        this.payee_case_references.value,
      )) {
        if (checked) {
          payee_case_references.push(key);
        }
      }
      const claim_references: string[] = [];
      for (const [key, checked] of Object.entries(
        this.claim_references.value,
      )) {
        if (checked) {
          claim_references.push(key);
        }
      }
      const creationStrategy = this.creation_strategy
        .value as CreationStrategyOption;

      const file = this.file.value as File;
      const claim_file = this.claim_file.value as File;

      const client = this.client.value as ListClientItem;

      const { bulk_upload } = await this.bulkUploadService.create({
        file: new File([file], file.name),
        claim_file: new File([claim_file], claim_file.name),
        creation_strategy: creationStrategy.value,
        name: this.name.value ?? '',
        client: client.uuid,
        partner: this.partner.value?.id ?? null,
        debtor_party_type_id: debtorPartyType?.id,
        claim_type_id: claimType?.id,
        legal_relationship_id: legalRelationship?.id,
        product_type: productType?.value,
        case_type: caseType?.value,
        interest_type: this.interest_type.value,
        column_associations,
        payee_case_references,
        claim_references,
      });

      this.bulkUpload = bulk_upload;
      if (!this.destroyed) {
        this.updateUrlWithBulkUploadId(bulk_upload.id.toString());
        this.pollStatus();
      }
    } catch (error: unknown | AxiosError) {
      let message = 'Valami hiba történt';
      if (error instanceof AxiosError) {
        console.error('Network error while submitting', error.response?.data);
        const errorData = errorResponse.safeParse(error.response?.data);
        if (errorData.success) {
          for (const [path, message] of Object.entries(errorData.data.errors)) {
            this.form.get(path)?.setErrors({
              unknown: message[0],
            });
          }

          this.expandForm();

          return;
        } else if (error.response?.data?.errors) {
          this.errorMessages = error.response.data.errors;
          this.onPageChange({
            pageIndex: 0,
            pageSize: 25,
            length: this.errorMessages.length,
          });

          return;
        }

        if (error.response?.data?.message) {
          message = error.response.data.message;
        }
      } else {
        console.error('Unknown error while submitting', error);
      }

      this.snackbar.open(message, 'OK', { duration: 5000 });
    } finally {
      this.validatingData = false;
      this.loading.set(false);
    }
  }

  async retryFailedJobs(): Promise<void> {
    if (this.bulkUpload) {
      this.bulkUpload = await this.bulkUploadService.retryFailedJobs(
        this.bulkUpload.id,
      );

      if (this.destroyed) {
        return;
      }

      this.pollDone();
      this.pollStatus();
    }
  }

  navigateToCases(): void {
    this.router.navigate(['/admin/cases-list'], {
      queryParams: {
        bulk_upload_name: this.bulkUpload?.name || this.name.value,
      },
    });
  }

  startNewBulkUpload(): void {
    // Reset form and component state
    this.pollDone();
    this.form.reset();
    this.bulkUpload = undefined;
    this.batchStatus = undefined;
    this.errorMessages = [];
    this.paginatedErrorMessages = [];
    this.validatingData = false;
    this.collapsed.set(false);

    // Clear URL parameters
    this.clearBulkUploadQueryParam();
  }

  getStateLabel(state: string): string {
    switch (state) {
      case 'validating':
        return 'Validálás';
      case 'starting':
        return 'Indítás';
      case 'started':
        return 'Elindítva';
      default:
        return state;
    }
  }

  private updateUrlWithBulkUploadId(bulk_upload_id: string): void {
    if (!this.isOnBulkUploadRoute()) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { bulk_upload_id },
      queryParamsHandling: 'merge',
    });
  }

  private stopPolling(): void {
    this.loading.set(false);
    clearInterval(this.batchInterval);
    this.batchInterval = undefined;
  }

  private pollDone(): void {
    this.stopPolling();
    if (!this.bulkUpload) {
      this.clearBulkUploadQueryParam();
    }
  }

  private clearBulkUploadQueryParam(): void {
    if (!this.isOnBulkUploadRoute()) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { bulk_upload_id: null },
      queryParamsHandling: 'merge',
    });
  }

  private isOnBulkUploadRoute(): boolean {
    return !this.destroyed && this.router.url.startsWith('/admin/bulk-upload');
  }

  private pollStatus(): void {
    this.batchInterval = setInterval(async () => {
      if (this.destroyed || this.loading()) {
        return;
      }

      if (!this.bulkUpload) {
        this.pollDone();
        return;
      }

      try {
        this.loading.set(true);

        await this.setResult();

        this.onPageChange({
          pageIndex: this.pageIndex,
          pageSize: this.pageSize,
          length: this.errorMessages.length,
        });
      } catch (error: unknown) {
        this.loading.set(false);
        let message = 'Valami hiba történt';
        if (error instanceof AxiosError && error.response?.data?.message) {
          message = error.response.data.message;
        }
        this.snackbar.open(message, 'OK', { duration: 5000 });
        this.pollDone();
        return;
      } finally {
        this.loading.set(false);
      }

      if (
        !this.batchStatus ||
        (this.batchStatus.pending_jobs <= 0 &&
          this.batchStatus.bulk_upload.state === 'started')
      ) {
        this.pollDone();
      }
    }, 1_000);
  }

  private async setResult(): Promise<void> {
    if (!this.bulkUpload) {
      return;
    }

    this.batchStatus = await this.bulkUploadService.getResult(
      this.bulkUpload.id,
    );
    this.bulkUpload = this.batchStatus.bulk_upload;

    this.errorMessages =
      this.batchStatus.errors?.map(
        (error) => `Sor ${error.row_index}: ${error.message}`,
      ) ?? [];
  }

  private requiredTrueInGroup(
    control: AbstractControl,
  ): ReturnType<ValidatorFn> {
    for (const value of Object.values(control.value)) {
      if (value) {
        return null;
      }
    }

    return {
      required: true,
    };
  }

  private async setPartnerOptions(name: string): Promise<void> {
    try {
      const result = await this.partnerService.listPartners({
        name,
      });

      this.partnerOptions = result.partners;
    } catch (error: unknown | AxiosError) {
      let message = 'Valami hiba történt';
      if (error instanceof AxiosError) {
        console.error(
          'Network error while getting partners',
          error.response?.data,
        );
        if (error.response?.data.message) {
          message = error.response.data.message;
        }
      } else {
        console.error('Unknown error while getting partners', error);
      }

      this.snackbar.open(message, 'OK', { duration: 5000 });
    }
  }

  private async setClientOptions(name: string): Promise<void> {
    try {
      const result = await this.clientsService.listClients({
        name,
      });

      this.clientOptions = result.clients;
    } catch (error: unknown | AxiosError) {
      let message = 'Valami hiba történt';
      if (error instanceof AxiosError) {
        console.error(
          'Network error while getting clients',
          error.response?.data,
        );
        if (error.response?.data.message) {
          message = error.response.data.message;
        }
      } else {
        console.error('Unknown error while getting clients', error);
      }

      this.snackbar.open(message, 'OK', { duration: 5000 });
    }
  }

  private async setPartyTypeOptions(): Promise<void> {
    try {
      this.partyTypeOptions = await this.enumsService.getEnum<PartyType>(
        partyType.model,
      );
    } catch (error: unknown | AxiosError) {
      let message = 'Valami hiba történt';
      if (error instanceof AxiosError) {
        console.error(
          'Network error while getting party types',
          error.response?.data,
        );
        if (error.response?.data.message) {
          message = error.response.data.message;
        }
      } else {
        console.error('Unknown error while getting party types', error);
      }

      this.snackbar.open(message, 'OK', { duration: 5000 });
    }
  }

  private async setClaimTypeOptions(): Promise<void> {
    try {
      this.claimTypeOptions = await this.enumsService.getEnum<ClaimType>(
        claimType.model,
      );
    } catch (error: unknown | AxiosError) {
      let message = 'Valami hiba történt';
      if (error instanceof AxiosError) {
        console.error(
          'Network error while getting claim types',
          error.response?.data,
        );
        if (error.response?.data.message) {
          message = error.response.data.message;
        }
      } else {
        console.error('Unknown error while getting claim types', error);
      }

      this.snackbar.open(message, 'OK', { duration: 5000 });
    }
  }

  private async setLegalRelationshipOptions(): Promise<void> {
    try {
      this.legalRelationshipOptions =
        await this.enumsService.getEnum<LegalRelationship>(
          legalRelationship.model,
        );
    } catch (error: unknown | AxiosError) {
      let message = 'Valami hiba történt';
      if (error instanceof AxiosError) {
        console.error(
          'Network error while getting legal relationships',
          error.response?.data,
        );
        if (error.response?.data.message) {
          message = error.response.data.message;
        }
      } else {
        console.error('Unknown error while getting legal relationships', error);
      }

      this.snackbar.open(message, 'OK', { duration: 5000 });
    }
  }

  private async extractHeadersFromFile(file: File): Promise<void> {
    const fileBuffer = await file.arrayBuffer();
    const workbook = xlsx.read(fileBuffer, {
      type: 'buffer',
      cellDates: true,
    });

    this.columnOptions = [];
    this.columnExampleValues = {};

    for (const sheetName of workbook.SheetNames) {
      const worksheet = workbook.Sheets[sheetName];
      const rows = xlsx.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: null,
        blankrows: false,
      }) as unknown[][];

      if (rows.length > 0) {
        const headerRow = rows[0] as string[];

        for (let i = 0; i < headerRow.length; ++i) {
          for (let j = i + 1; j < headerRow.length; ++j) {
            if (headerRow[i] === headerRow[j]) {
              const columnIdA = xlsx.utils.encode_col(i);
              const columnIdB = xlsx.utils.encode_col(j);
              this.snackbar.open(
                `${columnIdA} és a ${columnIdB} oszlopok nevei megegyeznek!`,
                'OK',
              );
              this.file.reset();
              return;
            }
          }
        }

        for (let i = 0; i < headerRow.length; ++i) {
          const columnId = xlsx.utils.encode_col(i);
          this.columnOptions.push({
            columnId,
            label: headerRow[i],
          });

          for (let rowIndex = 1; rowIndex < rows.length; ++rowIndex) {
            const example = rows[rowIndex][i];
            if (example) {
              if (example instanceof Date) {
                this.columnExampleValues[columnId] = format(
                  example,
                  'yyyy-MM-dd',
                );
              } else {
                this.columnExampleValues[columnId] = String(example);
              }
              break;
            }
          }
        }

        try {
          const guesses =
            await this.bulkUploadService.guessColumnAssociations(headerRow);
          for (const [column, label] of Object.entries(guesses)) {
            const value = this.columnOptions.find(
              (option) => option.label === label,
            );
            if (!value) {
              continue;
            }

            this.column_associations.controls[column]?.patchValue(value);
          }
        } catch (error: unknown) {
          console.error('Error while guessing column associations', error);
        }

        return;
      }
    }
  }

  private async setColumns(): Promise<void> {
    try {
      this.loading.set(true);

      const result = await this.bulkUploadService.listColumns();
      this.requiredColumns = result.required_columns;
      this.optionalColumns = result.optional_columns;

      for (const name of Object.keys(result.required_columns)) {
        this.addColumn(name, true);
      }

      for (const name of Object.keys(result.optional_columns)) {
        this.addColumn(name, false);
      }
    } catch (error: AxiosError | unknown) {
      let message = 'Valami hiba történt!';
      if (error instanceof AxiosError) {
        console.error(
          'Network error while getting columns',
          error.response?.data,
        );
        if (error.response?.data?.message) {
          message = error.response.data.message;
        }
      } else {
        console.error('Unknown error while getting columns', error);
      }

      this.snackbar.open(message, 'OK', { duration: 5000 });
    } finally {
      this.loading.set(false);
    }
  }

  private addColumn(name: string, required: boolean): void {
    const columnAssociationControl = new FormControl<ColumnOption | null>(null);
    if (required) {
      columnAssociationControl.addValidators(Validators.required);
    }
    this.column_associations.setControl(name, columnAssociationControl);
    this.payee_case_references.setControl(
      name,
      new FormControl(false, { nonNullable: true }),
    );
    this.claim_references.setControl(
      name,
      new FormControl(false, { nonNullable: true }),
    );
  }
}
